import { useCallback, useEffect, useState } from 'react';
import { api } from './api';

interface Fav {
  title: string;
  artist: string;
}
const key = (t: string, a: string) => `${t.trim()}|${a.trim()}`.toLowerCase();

// several components each mount their own instance — broadcast toggles so they stay in sync
const listeners = new Set<() => void>();

export function useFavorites() {
  const [tracks, setTracks] = useState<Fav[]>([]);
  const [keys, setKeys] = useState<Set<string>>(new Set());

  const reload = useCallback(() => {
    api
      .favorites()
      .then((d) => {
        const ts = d.tracks ?? [];
        setTracks(ts);
        setKeys(new Set(ts.map((t) => key(t.title, t.artist))));
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    reload();
    listeners.add(reload);
    return () => {
      listeners.delete(reload);
    };
  }, [reload]);

  const isLiked = useCallback((t?: string | null, a?: string | null) => !!t && keys.has(key(t, a ?? '')), [keys]);

  const toggle = useCallback(
    async (t: string, a: string) => {
      if (!t) return;
      const k = key(t, a);
      // optimistic update; reconcile from the server if it errors
      setKeys((prev) => {
        const n = new Set(prev);
        if (n.has(k)) n.delete(k);
        else n.add(k);
        return n;
      });
      setTracks((prev) =>
        prev.some((x) => key(x.title, x.artist) === k)
          ? prev.filter((x) => key(x.title, x.artist) !== k)
          : [{ title: t, artist: a }, ...prev],
      );
      try {
        await api.toggleFavorite(t, a);
        for (const l of listeners) if (l !== reload) l();
      } catch {
        reload();
      }
    },
    [reload],
  );

  return { tracks, isLiked, toggle, reload };
}
